import { useState, useCallback } from 'react'
import { trpc } from '@/config/trpc'
import { getPlatformDisplayName } from '@/lib/calendar/types'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { AlertCircle, Check, Loader2, RotateCcw, Download } from 'lucide-react'
import { toast } from 'sonner'
import { captureError } from '@/lib/errors'

type ImportResult = {
  imported: number
  skipped: number
  total: number
}

type AccountEntry = {
  platform: PublishablePlatformType
  accountId: string
  displayName: string
  autoImport?: boolean
}

type AccountImportStatus =
  | { status: 'pending' }
  | { status: 'importing' }
  | { status: 'done'; result: ImportResult }
  | { status: 'error'; message: string }

type ModalState = 'select' | 'importing' | 'done'

const accountKey = (pa: AccountEntry) => `${pa.platform}:${pa.accountId}`

const formatLastImported = (date: Date) => {
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Imported today'
  if (diffDays === 1) return 'Imported yesterday'
  if (diffDays < 30) return `Imported ${diffDays} days ago`
  return `Imported on ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

export function ImportPostsModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [state, setState] = useState<ModalState>('select')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [accountStatuses, setAccountStatuses] = useState<Map<string, AccountImportStatus>>(
    new Map(),
  )

  const utils = trpc.useUtils()
  const { mutateAsync: importPosts } = trpc.import.importPosts.useMutation()
  const { mutateAsync: setAutoImportMut } = trpc.import.setAutoImport.useMutation()
  const { data: importInfo } = trpc.import.getImportInfo.useQuery()

  // Fetch connected platform accounts
  const { data: linkedinData } = trpc.platform.isAuthenticated.useQuery({ platform: 'linkedin' })
  const { data: instagramData } = trpc.platform.isAuthenticated.useQuery({ platform: 'instagram' })
  const { data: tiktokData } = trpc.platform.isAuthenticated.useQuery({ platform: 'tiktok' })
  const { data: youtubeData } = trpc.platform.isAuthenticated.useQuery({ platform: 'youtube' })
  const { data: facebookData } = trpc.platform.isAuthenticated.useQuery({ platform: 'facebook' })

  const platformAccounts: AccountEntry[] = []
  const analyticsUnavailable = new Set<PublishablePlatformType>()

  const addAccounts = (
    platform: PublishablePlatformType,
    data:
      | {
          isAuthenticated: boolean
          analyticsAvailable?: boolean
          accounts?: { id: string; displayName: string; autoImport?: boolean }[]
        }
      | undefined,
  ) => {
    if (data?.isAuthenticated && 'accounts' in data && data.accounts) {
      if (data.analyticsAvailable === false) analyticsUnavailable.add(platform)
      for (const acc of data.accounts) {
        if (platform === 'linkedin' && !acc.id.startsWith('urn:li:organization:')) continue
        platformAccounts.push({
          platform,
          accountId: acc.id,
          displayName: acc.displayName,
          autoImport: acc.autoImport,
        })
      }
    }
  }

  addAccounts('linkedin', linkedinData)
  addAccounts('instagram', instagramData)
  addAccounts('tiktok', tiktokData)
  addAccounts('youtube', youtubeData)
  addAccounts('facebook', facebookData)

  const getLastImported = (platform: PublishablePlatformType, accountId: string): Date | null => {
    const key = `${platform}:${accountId}`
    const ts = importInfo?.cursorMap[key]
    return ts ? new Date(ts) : null
  }

  const toggleAccount = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const handleImport = async (retryOnly = false) => {
    const accounts = platformAccounts.filter((pa) => {
      const key = accountKey(pa)
      if (retryOnly) {
        return accountStatuses.get(key)?.status === 'error'
      }
      return selectedKeys.has(key)
    })

    if (accounts.length === 0) return

    setState('importing')

    // Initialize statuses
    const initial = new Map(accountStatuses)
    for (const acc of accounts) {
      initial.set(accountKey(acc), { status: 'pending' })
    }
    setAccountStatuses(initial)

    let totalImported = 0

    for (const acc of accounts) {
      const key = accountKey(acc)
      setAccountStatuses((prev) => new Map(prev).set(key, { status: 'importing' }))

      try {
        const result = await importPosts({ platform: acc.platform, accountId: acc.accountId })
        totalImported += result.imported
        setAccountStatuses((prev) => new Map(prev).set(key, { status: 'done', result }))
      } catch (error) {
        captureError(error, {
          flow: 'import-posts.perAccount',
          platform: acc.platform,
          accountId: acc.accountId,
          retry: retryOnly,
        })
        const msg = error instanceof Error ? error.message : String(error)
        setAccountStatuses((prev) => new Map(prev).set(key, { status: 'error', message: msg }))
      }
    }

    setState('done')
    utils.trackedPost.list.invalidate()
    utils.import.getImportInfo.invalidate()
    utils.analytics.overview.invalidate()
    utils.analytics.accountOverview.invalidate()
    if (totalImported > 0) {
      toast.success(
        `Imported ${totalImported} post${totalImported === 1 ? '' : 's'} from ${accounts.length} account${accounts.length === 1 ? '' : 's'}`,
      )
    }
  }

  const handleToggleAutoImport = async (account: AccountEntry, enabled: boolean) => {
    try {
      await setAutoImportMut({
        platform: account.platform,
        accountId: account.accountId,
        autoImport: enabled,
      })
      utils.platform.isAuthenticated.invalidate({ platform: account.platform })
    } catch (error) {
      captureError(error, {
        flow: 'import-posts.toggleAutoImport',
        platform: account.platform,
        accountId: account.accountId,
        enabled,
      })
      toast.error('Failed to update auto-import setting')
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(() => {
      setState('select')
      setSelectedKeys(new Set())
      setAccountStatuses(new Map())
    }, 200)
  }

  const hasFailures = [...accountStatuses.values()].some((s) => s.status === 'error')

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Posts</DialogTitle>
          <DialogDescription>
            {state === 'select' && 'Select accounts to import posts from.'}
            {state === 'importing' && 'Importing posts from your accounts...'}
            {state === 'done' &&
              (hasFailures ? 'Import finished with errors.' : 'Import complete.')}
          </DialogDescription>
        </DialogHeader>

        {state === 'select' && (
          <div className="flex flex-col gap-2">
            {platformAccounts.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                No platforms connected. Connect a platform first to import posts.
              </p>
            ) : (
              <>
                {platformAccounts.map((pa) => {
                  const key = accountKey(pa)
                  const lastImported = getLastImported(pa.platform, pa.accountId)
                  const needsReauth = analyticsUnavailable.has(pa.platform)
                  const isSelected = selectedKeys.has(key)

                  return (
                    <button
                      key={key}
                      disabled={needsReauth}
                      onClick={() => toggleAccount(key)}
                      className={`flex items-center rounded-lg border-2 px-3 py-3 text-left transition-colors ${
                        needsReauth
                          ? 'cursor-not-allowed border-transparent opacity-60'
                          : isSelected
                            ? 'border-primary bg-primary/5'
                            : 'hover:bg-hover border-transparent'
                      }`}>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{getPlatformDisplayName(pa.platform)}</span>
                          <span className="text-muted-foreground text-sm">{pa.displayName}</span>
                        </div>
                        {needsReauth ? (
                          <span className="text-xs text-amber-600 dark:text-amber-400">
                            Reconnect account to enable imports
                          </span>
                        ) : (
                          lastImported && (
                            <span className="text-muted-foreground text-xs">
                              {formatLastImported(lastImported)}
                            </span>
                          )
                        )}
                      </div>
                      {!needsReauth && (
                        <div
                          className="flex items-center gap-1.5"
                          onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            id={`auto-${key}`}
                            checked={pa.autoImport ?? false}
                            onCheckedChange={(checked) =>
                              handleToggleAutoImport(pa, checked === true)
                            }
                          />
                          <label
                            htmlFor={`auto-${key}`}
                            className="text-muted-foreground cursor-pointer text-xs">
                            Auto
                          </label>
                        </div>
                      )}
                    </button>
                  )
                })}

                <Button
                  className="mt-2"
                  disabled={selectedKeys.size === 0}
                  onClick={() => handleImport()}>
                  <Download className="mr-2 h-4 w-4" />
                  {selectedKeys.size === 0
                    ? 'Select accounts to import'
                    : `Import from ${selectedKeys.size} account${selectedKeys.size === 1 ? '' : 's'}`}
                </Button>
              </>
            )}
          </div>
        )}

        {(state === 'importing' || state === 'done') && (
          <div className="flex flex-col gap-2">
            {platformAccounts
              .filter((pa) => accountStatuses.has(accountKey(pa)))
              .map((pa) => {
                const key = accountKey(pa)
                const status = accountStatuses.get(key)!

                return (
                  <div key={key} className="flex items-center gap-3 rounded-lg border px-3 py-3">
                    {/* Status icon */}
                    <div className="flex-shrink-0">
                      {status.status === 'pending' && (
                        <div className="bg-muted h-5 w-5 rounded-full" />
                      )}
                      {status.status === 'importing' && (
                        <Loader2 className="text-primary h-5 w-5 animate-spin" />
                      )}
                      {status.status === 'done' && (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
                          <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                        </div>
                      )}
                      {status.status === 'error' && (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50">
                          <AlertCircle className="h-3 w-3 text-red-600 dark:text-red-400" />
                        </div>
                      )}
                    </div>

                    {/* Account info + result */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {getPlatformDisplayName(pa.platform)}
                        </span>
                        <span className="text-muted-foreground truncate text-xs">
                          {pa.displayName}
                        </span>
                      </div>
                      {status.status === 'importing' && (
                        <p className="text-muted-foreground text-xs">Importing...</p>
                      )}
                      {status.status === 'done' && (
                        <p className="text-muted-foreground text-xs">
                          {status.result.imported > 0
                            ? `${status.result.imported} imported`
                            : 'All up to date'}
                          {status.result.skipped > 0 &&
                            `, ${status.result.skipped} already tracked`}
                        </p>
                      )}
                      {status.status === 'error' && (
                        <p className="truncate text-xs text-red-600 dark:text-red-400">
                          {status.message}
                        </p>
                      )}
                    </div>

                    {/* Auto-import toggle on success */}
                    {state === 'done' && status.status === 'done' && (
                      <div
                        className="flex items-center gap-1.5"
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          id={`auto-done-${key}`}
                          checked={pa.autoImport ?? false}
                          onCheckedChange={(checked) =>
                            handleToggleAutoImport(pa, checked === true)
                          }
                        />
                        <label
                          htmlFor={`auto-done-${key}`}
                          className="text-muted-foreground cursor-pointer text-xs">
                          Auto
                        </label>
                      </div>
                    )}
                  </div>
                )
              })}

            {state === 'done' && (
              <div className="mt-2 flex gap-2">
                {hasFailures && (
                  <Button variant="outline" className="gap-2" onClick={() => handleImport(true)}>
                    <RotateCcw className="h-4 w-4" />
                    Retry failed
                  </Button>
                )}
                <Button className="flex-1" onClick={handleClose}>
                  Done
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
